// TraitementConges.jsx (RH/Admin) — Redesigné avec le style TraitementAvances

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    AlertCircle,
    FileText,
    ChevronDown,
    CalendarDays,
    Loader,
    Eye,
    Download,
    RotateCcw,
} from "lucide-react";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import latoRegularUrl from "../../../../fonts/Lato-Regular.ttf";
import latoBoldUrl from "../../../../fonts/Lato-Bold.ttf";
import { API_BASE_URL } from "../../../../../api";
import {
    getAdminInboxRequest,
    adminDecisionRequest,
    clearLeaveRequestMessage,
    exportLeaveRequestsExcelRequest,
    adjustLeaveRequestPeriodRequest,
} from "../../../../redux/actions/leaveRequestsActions";
import {
    getWorkdaysConfigRequest,
    getCalendarSettingsRequest,
} from "../../../../redux/actions/workDaysActions";
import "../TraitementStyles/TraitementNotesFrais.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import { getRequestTreatedBy } from "./treatmentDecisionInfo";

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => { setQuery(opt.label); onChange(opt.value); setOpen(false); };
    const handleInputChange = (e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); };
    const handleClear = () => { setQuery(""); onChange(""); setOpen(false); };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Effacer")}>
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Afficher la liste")}>
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    {t("Aucun résultat")}
                </div>
            )}
        </div>
    );
};

const TraitementConges = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    // Redux state
    const leaveState = useSelector((state) => state.leaveRequests || {});
    const { workdaysConfig, calendarSettings } = useSelector((state) => state.workDays || {});
    const loading = !!leaveState.loading;
    const apiError = leaveState.error;
    const inbox = leaveState.adminInbox || [];

    const [activeTab, setActiveTab] = useState("enCours");

    // Filtres
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDateDebut, setFilterDateDebut] = useState("");
    const [filterDateFin, setFilterDateFin] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDateDebut("");
        setFilterDateFin("");
    };

    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [decisionComment, setDecisionComment] = useState("");
    const [pendingDecision, setPendingDecision] = useState(null); // "approve" | "reject"
    const [previewDoc, setPreviewDoc] = useState(null); // { url, name, path }
    const [closedLeaveFolders, setClosedLeaveFolders] = useState(new Set());
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [adjustStartDate, setAdjustStartDate] = useState("");
    const [adjustEndDate, setAdjustEndDate] = useState("");
    const [adjustDateReprise, setAdjustDateReprise] = useState("");

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminInboxRequest());
        dispatch(getWorkdaysConfigRequest());
        dispatch(getCalendarSettingsRequest(new Date().getFullYear()));
    }, [dispatch]);

    // Synchronise automatiquement la liste RH avec les nouvelles demandes
    // sans solliciter l'API lorsque l'onglet n'est pas visible.
    useEffect(() => {
        const refreshInbox = () => {
            if (document.visibilityState === "visible") {
                dispatch(getAdminInboxRequest({ silent: true }));
            }
        };

        const intervalId = window.setInterval(refreshInbox, 5_000);
        document.addEventListener("visibilitychange", refreshInbox);
        window.addEventListener("focus", refreshInbox);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", refreshInbox);
            window.removeEventListener("focus", refreshInbox);
        };
    }, [dispatch]);

    const fixedHolidays = calendarSettings?.fixed || [];
    const hijriHolidays = calendarSettings?.hijri || [];
    const customHolidays = calendarSettings?.custom || [];

    const checkIsHoliday = (d) => {
        if (!d) return false;
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        const isFixed = (fixedHolidays || []).some((h) => h.date === dateStr && h.selected);
        if (isFixed) return true;

        const isHijri = (hijriHolidays || []).some((h) => {
            if (!h.selected || !h.date) return false;
            const holidayDate = new Date(h.date);
            const checkDate = new Date(d);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);

                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
        if (isHijri) return true;

        const fullDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return (customHolidays || []).some((h) => h.date === fullDate);
    };

    const getProchainJourOuvre = (dateStr) => {
        if (!dateStr) return "";
        let d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "";

        d.setDate(d.getDate() + 1);

        let safetyCount = 0;
        while (safetyCount < 30) {
            const dayIndex = d.getDay();
            const dayName = dayIndex === 0 ? "dimanche" : 
                            dayIndex === 1 ? "lundi" : 
                            dayIndex === 2 ? "mardi" : 
                            dayIndex === 3 ? "mercredi" : 
                            dayIndex === 4 ? "jeudi" : 
                            dayIndex === 5 ? "vendredi" : "samedi";

            let isWorking = true;
            if (workdaysConfig && workdaysConfig.workDays && workdaysConfig.workDays[dayName]) {
                const cfg = workdaysConfig.workDays[dayName];
                isWorking = typeof cfg.isWorking === "boolean" ? cfg.isWorking : 
                            typeof cfg.working === "boolean" ? cfg.working : true;
            } else {
                isWorking = dayIndex !== 0 && dayIndex !== 6;
            }

            if (isWorking && !checkIsHoliday(d)) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const dayVal = String(d.getDate()).padStart(2, "0");
                return `${y}-${m}-${dayVal}`;
            }
            d.setDate(d.getDate() + 1);
            safetyCount++;
        }
        return "";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const isPostponementRejected = (d) =>
        d.originalDateDebut &&
        String(d.status || "").toUpperCase() === "APPROVED" &&
        (d.adminDecision === false || (d.managerDecision === false && d.adminDecision == null));

    const mapStatusToTab = (d) => {
        const v = String(d.status || "").toUpperCase();
        if (v === "PENDING_ADMIN") return "enCours";
        if (v === "POSTPONED") return "approuve";
        if (v === "APPROVED") {
            if (isPostponementRejected(d)) return "rejete";
            return "approuve";
        }
        if (v === "REJECTED") return "rejete";
        if (v === "CANCELLED") return "annule";
        return null;
    };

    const demandes = useMemo(() => {
        return (inbox || [])
            .map((d) => {
                const tab = mapStatusToTab(d);
                if (!tab) return null;
                return {
                    ...d,
                    id: d.id,
                    matricule: d.employeeCode || "—",
                    nom: d.employeeName || "—",
                    typeConge: d.absenceTypeNom || "—",
                    previousRequestId: d.previousRequestId || null,
                    dateDebut: d.dateDebut,
                    dateFin: d.dateFin,
                    dateReprise: d.dateReprise || null,
                    duree: d.duree ?? 0,
                    motif: d.commentaire || " - ",
                    justificatif: d.justificatif || null,
                    statut: tab,
                    rawStatus: d.status,
                    managerDecision: d.managerDecision ?? null,
                    managerName: d.managerName || null,
                    managerDecisionAt: d.managerDecisionAt || null,
                    managerComment: d.managerComment || null,
                    adminDecision: d.adminDecision ?? null,
                    adminName: d.adminName || null,
                    adminDecisionAt: d.adminDecisionAt || null,
                    adminComment: d.adminComment || null,
                    originalDateDebut: d.originalDateDebut,
                    originalDateFin: d.originalDateFin,
                    dateDemande: d.createdAt,
                    justificatifs: d.justificatifs || [],
                };
            })
            .filter(Boolean);
    }, [inbox]);

    const linkedRequestLabel = (demande) => {
        if (demande.previousRequestId) {
            return `Dossier congé #${demande.previousRequestId} · complément`;
        }
        const child = demandes.find((item) => item.previousRequestId === demande.id);
        return child ? `Dossier congé #${demande.id} · lié` : null;
    };

    const formatPdfDate = (date) => {
        if (!date) return "—";
        const parts = String(date).slice(0, 10).split("-");
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(date);
    };

    const formatPdfNumber = (value) => Number(value || 0).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const getDernierConge = (demande) => (inbox || [])
        .filter((item) => item.employeeId === demande.employeeId)
        .filter((item) => ["APPROVED", "POSTPONED"].includes(String(item.status || "").toUpperCase()))
        .filter((item) => item.id !== demande.id && item.dateFin && item.dateFin < demande.dateDebut)
        .sort((a, b) => String(b.dateFin).localeCompare(String(a.dateFin)))[0];

    const handleDownloadLeavePdf = async (demande) => {
        try {
            const doc = new jsPDF({ unit: "mm", format: "a4" });
            let pdfFont = "helvetica";
            try {
                const toBase64 = (buffer) => {
                    const bytes = new Uint8Array(buffer);
                    let binary = "";
                    for (let i = 0; i < bytes.length; i += 0x8000) {
                        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
                    }
                    return btoa(binary);
                };
                const [regular, bold] = await Promise.all([
                    fetch(latoRegularUrl).then((response) => response.arrayBuffer()),
                    fetch(latoBoldUrl).then((response) => response.arrayBuffer()),
                ]);
                doc.addFileToVFS("Lato-Regular.ttf", toBase64(regular));
                doc.addFileToVFS("Lato-Bold.ttf", toBase64(bold));
                doc.addFont("Lato-Regular.ttf", "Lato", "normal");
                doc.addFont("Lato-Bold.ttf", "Lato", "bold");
                pdfFont = "Lato";
            } catch (fontError) {
                console.warn("Police Lato indisponible pour le PDF, police de secours utilisée.", fontError);
            }
            const pageWidth = 210;
            const margin = 12;
            const right = pageWidth - margin;
            const lastLeave = getDernierConge(demande);
            const calendarDays = demande.dateDebut && demande.dateFin
                ? Math.floor((new Date(`${demande.dateFin}T12:00:00`) - new Date(`${demande.dateDebut}T12:00:00`)) / 86400000) + 1
                : 0;
            const decisionDate = demande.adminDecisionAt || demande.managerDecisionAt || demande.updatedAt || demande.createdAt;
            // Espace renforcé pour l'en-tête de l'entreprise avant le contenu de la fiche.
            let y = 62;

            const line = (at = y) => doc.line(margin, at, right, at);
            const centered = (text, at, size = 12, bold = true) => {
                doc.setFont(pdfFont, bold ? "bold" : "normal");
                doc.setFontSize(size);
                doc.text(text, pageWidth / 2, at, { align: "center" });
                if (bold) {
                    const width = doc.getTextWidth(text);
                    doc.setLineWidth(0.35);
                    doc.line((pageWidth - width) / 2, at + 1.2, (pageWidth + width) / 2, at + 1.2);
                    doc.setLineWidth(0.2);
                }
            };
            const section = (title, { topLine = true, bottomLine = true } = {}) => {
                if (topLine) line(y);
                y += 5;
                centered(title, y, 12, true);
                y += 2;
                if (bottomLine) line(y);
                y += 3;
            };
            const text = (value, x, at, size = 9, bold = false) => {
                doc.setFont(pdfFont, bold ? "bold" : "normal");
                doc.setFontSize(size);
                doc.text(String(value || "—"), x, at);
            };
            const labelValue = (label, value, x, at, offset = 33) => {
                text(`${label} :`, x, at);
                text(value, x + offset, at, 9.5, true);
            };
            const borderedRow = (cells, at, height = 5) => {
                const total = right - margin;
                let x = margin;
                cells.forEach((cell) => {
                    const width = total * cell.width;
                    doc.rect(x, at, width, height);
                    doc.setFont(pdfFont, cell.bold ? "bold" : "normal");
                    doc.setFontSize(cell.size || 8.5);
                    doc.text(String(cell.value || "—"), x + width / 2, at + height / 2 + 1.1, { align: "center" });
                    x += width;
                });
            };

            doc.setTextColor(0, 0, 0);
            text(`${demande.companyCity || ""} le :`, 150, y, 8);
            text(formatPdfDate(decisionDate), 174, y, 8, true);
            y += 8;
            doc.setFont("times", "bold");
            doc.setFontSize(16);
            doc.text("DECISION DE CONGE", pageWidth / 2, y, { align: "center" });
            const decisionTitleWidth = doc.getTextWidth("DECISION DE CONGE");
            doc.setLineWidth(0.35);
            doc.line((pageWidth - decisionTitleWidth) / 2, y + 1.4, (pageWidth + decisionTitleWidth) / 2, y + 1.4);
            doc.setLineWidth(0.2);
            y += 6;

            section("Demandeur");
            labelValue("Nom et prénom", demande.employeeName, 12, y); labelValue("Matricule", demande.employeeCode, 112, y, 22); y += 5;
            labelValue("Poste", demande.employeeJobName, 12, y); labelValue("Service", demande.employeeEntityName, 112, y, 18); y += 5;
            labelValue("Date de naissance", formatPdfDate(demande.employeeDateOfBirth), 12, y); labelValue("CIN", demande.employeeCin, 112, y, 12); y += 5;
            labelValue("Date d'embauche", formatPdfDate(demande.employeeHireDate), 12, y); labelValue("CNSS", demande.employeeCnss, 112, y, 15); y += 6;

            section("Droit au Congé", { bottomLine: false });
            const annualBalance = demande.annualLeaveBalance ?? demande.soldeInitial;
            const requestCategory = String(demande.absenceCategory || demande.category || "").toUpperCase();
            // Le fallback couvre les demandes déjà enregistrées avant l'ajout de la catégorie dans la réponse API.
            const isSpecialLeave = requestCategory === "SPECIAL"
                || (!requestCategory && (demande.soldeConsomme === null || demande.soldeConsomme === undefined || Number(demande.soldeConsomme) === 0));
            const balanceBeforeRequest = isSpecialLeave ? annualBalance : (demande.soldeInitial ?? annualBalance);
            const balanceAfterRequest = isSpecialLeave ? annualBalance : demande.soldeRestant;
            borderedRow([{ value: "Solde disponible avant la demande", width: .83, bold: true }, { value: formatPdfNumber(balanceBeforeRequest), width: .17, bold: true }], y); y += 6;
            borderedRow([{ value: "Jours demandés", width: .83, bold: true }, { value: formatPdfNumber(demande.duree), width: .17, bold: true }], y); y += 6;
            borderedRow([{ value: "Reste du droit après la demande", width: .83, bold: true }, { value: formatPdfNumber(balanceAfterRequest), width: .17, bold: true }], y); y += 10;

            section("Dernier Congé Pris", { topLine: false, bottomLine: false });
            borderedRow([{ value: "Date de début", width: .22, bold: true }, { value: "Date de fin", width: .22, bold: true }, { value: "Date de reprise", width: .22, bold: true }, { value: "Jours déductibles", width: .20, bold: true }, { value: "Jours calendaires", width: .14, bold: true }], y); y += 5;
            const lastCalendar = lastLeave ? Math.floor((new Date(`${lastLeave.dateFin}T12:00:00`) - new Date(`${lastLeave.dateDebut}T12:00:00`)) / 86400000) + 1 : "—";
            borderedRow([{ value: formatPdfDate(lastLeave?.dateDebut), width: .22 }, { value: formatPdfDate(lastLeave?.dateFin), width: .22 }, { value: formatPdfDate(lastLeave?.dateReprise), width: .22 }, { value: lastLeave?.duree ?? "—", width: .20 }, { value: lastCalendar, width: .14 }], y); y += 10;

            section("Congé demandé", { topLine: false, bottomLine: false });
            borderedRow([{ value: "Date de début", width: .33, bold: true }, { value: "Date de fin", width: .33, bold: true }, { value: "Date de reprise", width: .34, bold: true }], y); y += 5;
            borderedRow([{ value: formatPdfDate(demande.dateDebut), width: .33 }, { value: formatPdfDate(demande.dateFin), width: .33 }, { value: formatPdfDate(demande.dateReprise), width: .34 }], y); y += 10;
            labelValue("Nombre de jours ouvrables pris", `${demande.duree || 0} jour(s)`, 12, y, 48); y += 6;
            labelValue("Nombre de jours calendaires", `${calendarDays} jour(s)`, 12, y, 48); y += 9;

            section("Remplaçant/intérim");
            y += 2;
            labelValue("Nom du remplaçant", demande.remplacantName || "Non désigné", 12, y, 38); labelValue("Matricule", demande.remplacantCode, 112, y, 22); y += 7;
            labelValue("Poste", demande.remplacantJobName, 12, y, 16); y += 6;
            doc.rect(margin, y - 3, 3, 3);
            doc.setFont(pdfFont, "normal"); doc.setFontSize(8);
            const passation = doc.splitTextToSize("En cochant cette case, le concerné et son remplaçant confirment que la passation a été faite en bonne et due forme.", right - margin - 6);
            doc.text(passation, margin + 5, y);
            y += passation.length * 3 + 6;

            section("Signatures");
            const totalWidth = right - margin;
            const signaturesWidth = totalWidth * 0.52;
            const signatureWidth = signaturesWidth / 2;
            const notesX = margin + signaturesWidth + 4;
            const notesWidth = right - notesX;
            doc.rect(margin, y, signatureWidth, 23);
            doc.rect(margin + signatureWidth, y, signatureWidth, 23);
            doc.setFont(pdfFont, "bold"); doc.setFontSize(9.5);
            doc.text("Demandeur", margin + signatureWidth / 2, y + 7, { align: "center" });
            doc.text("DRH", margin + signatureWidth + signatureWidth / 2, y + 7, { align: "center" });
            let notesY = y + 5;
            doc.setFont(pdfFont, "normal"); doc.setFontSize(7.5);
            const notes = [
                "* Les journées de Samedi sont considérées journées ouvrables.",
                "* La demande du congé doit parvenir au service personnel DIX (10) jours avant la date de départ du bénéficiaire.",
                "* Le congé sans solde est soumis à l’approbation de la Direction Générale.",
            ];
            notes.forEach((note) => {
                const wrapped = doc.splitTextToSize(note, notesWidth);
                doc.text(wrapped, notesX, notesY);
                notesY += wrapped.length * 3.4 + 2;
            });
            text("Observation :", margin, y + 27, 8.5, true);

            doc.save(`Fiche_conge_${demande.employeeCode || demande.id}.pdf`);
        } catch (error) {
            console.error("Erreur de génération de la fiche de congé", error);
            Swal.fire({ icon: "error", title: t("Erreur"), text: t("La fiche de congé n'a pas pu être générée.") });
        }
    };

    const getStatutBadge = (demande) => {
        const raw = String(demande.rawStatus || "").toUpperCase();
        if (raw === "POSTPONEMENT_REQUEST" || raw === "POSTPONED_REQUEST") {
            return (
                <span className="tcBadge" style={{ backgroundColor: "#f0f9ff", color: "#0c4a6e" }}>
                    En attente de report
                </span>
            );
        }
        if (raw === "POSTPONED") {
            return (
                <span className="tcBadge" style={{ backgroundColor: "#d4edda", color: "#155724", display: "flex", alignItems: "center", gap: 4 }}>
                    Approuvée
                    <span style={{ background: "#0369a1", color: "#fff", borderRadius: 4, fontSize: 10, padding: "1px 5px", fontWeight: 700 }}>{t("Après report")}</span>
                </span>
            );
        }
        if (isPostponementRejected(demande)) {
            return (
                <span className="tcBadge" style={{ backgroundColor: "#f8d7da", color: "#721c24", display: "flex", alignItems: "center", gap: 4 }}>
                    Rejetée
                    <span style={{ background: "#dc2626", color: "#fff", borderRadius: 4, fontSize: 10, padding: "1px 5px", fontWeight: 700 }}>{t("Après report")}</span>
                </span>
            );
        }
        const config = {
            enCours: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: { label: "Approuvée", bg: "#d4edda", color: "#155724" },
            rejete: { label: "Rejetée", bg: "#f8d7da", color: "#721c24" },
            annule: { label: "Annulée", bg: "#f3f4f6", color: "#6b7280" },
        };
        const c = config[demande.statut] || config.enCours;
        return (
            <span className="tcBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const collaborateurOptions = useMemo(() => {
        const map = new Map();
        demandes.forEach((d) => {
            if (d.nom && !map.has(d.nom)) {
                map.set(d.nom, { value: d.nom, label: d.matricule ? `${d.nom} (${d.matricule})` : d.nom });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [demandes]);

    const typeCongeOptions = useMemo(() => {
        return [...new Set(demandes.map((d) => d.typeConge).filter(Boolean))]
            .sort()
            .map((t) => ({ value: t, label: t }));
    }, [demandes]);

    const getFilteredDemandes = () => {
        let filtered = demandes.filter((d) => d.statut === activeTab);

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.nom && d.nom.toLowerCase().includes(val)) ||
                    (d.matricule && d.matricule.toLowerCase().includes(val))
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((d) => (d.typeConge || "").toLowerCase().includes(val));
        }
        if (filterDateDebut) {
            filtered = filtered.filter((d) => {
                if (!d.dateDebut) return false;
                return new Date(d.dateDebut).toISOString().split("T")[0] === filterDateDebut;
            });
        }
        if (filterDateFin) {
            filtered = filtered.filter((d) => {
                if (!d.dateFin) return false;
                return new Date(d.dateFin).toISOString().split("T")[0] === filterDateFin;
            });
        }
        return filtered;
    };

    const tabs = useMemo(() => {
        return [
            {
                id: "enCours",
                label: "En cours",
                count: demandes.filter((d) => d.statut === "enCours").length,
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: demandes.filter((d) => d.statut === "approuve").length,
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: demandes.filter((d) => d.statut === "rejete").length,
            },
            {
                id: "annule",
                label: "Annulées",
                count: demandes.filter((d) => d.statut === "annule").length,
            },
        ];
    }, [demandes]);

    const handleOpenDecision = (demande, decision) => {
        if (decision === "approve") {
            Swal.fire({
                title: t("Confirmation"),
                text: t("Êtes-vous sûr de vouloir approuver cette demande de congé ?"),
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#10b981",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Oui, approuver",
                cancelButtonText: "Annuler",
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(
                        adminDecisionRequest({
                            id: demande.id,
                            approved: true,
                            comment: "",
                        })
                    );
                    dispatch(clearLeaveRequestMessage());
                }
            });
            return;
        }

        setSelectedDemande(demande);
        setPendingDecision(decision);
        setDecisionComment("");
        setShowDecisionModal(true);
    };

    const confirmDecision = () => {
        if (!selectedDemande) return;
        const approved = pendingDecision === "approve";
        if (!approved && !decisionComment.trim()) return;

        dispatch(
            adminDecisionRequest({
                id: selectedDemande.id,
                approved,
                comment: decisionComment || "",
            })
        );
        dispatch(clearLeaveRequestMessage());
        closeDecision();
    };

    const closeDecision = () => {
        setShowDecisionModal(false);
        setDecisionComment("");
        setSelectedDemande(null);
        setPendingDecision(null);
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const handleOpenAdjust = (demande) => {
        setSelectedDemande(demande);
        setAdjustStartDate(demande.dateDebut);
        setAdjustEndDate(demande.dateFin);
        setAdjustDateReprise(demande.dateFin ? getProchainJourOuvre(demande.dateFin) : (demande.dateReprise || ""));
        setShowAdjustModal(true);
    };

    const confirmAdjust = () => {
        if (!selectedDemande || !adjustStartDate || !adjustEndDate) return;
        dispatch(
            adjustLeaveRequestPeriodRequest({
                id: selectedDemande.id,
                startDate: adjustStartDate,
                endDate: adjustEndDate,
                dateReprise: adjustDateReprise,
            })
        );
        closeAdjust();
    };

    const closeAdjust = () => {
        setShowAdjustModal(false);
        setSelectedDemande(null);
        setAdjustStartDate("");
        setAdjustEndDate("");
        setAdjustDateReprise("");
    };

    const handlePreview = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}&inline=true`;
        setPreviewDoc({
            url,
            name: path.split("/").pop(),
            path: path
        });
    };

    const closePreview = () => setPreviewDoc(null);

    const handleDownload = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}`;
        window.open(url, "_blank");
    };

    const safeErrorText = (err) => {
        if (!err) return "";
        if (typeof err === "string") return err;
        if (err?.message) return String(err.message);
        try { return JSON.stringify(err); } catch { return String(err); }
    };

    const filtered = getFilteredDemandes();

    const leaveRows = (() => {
        const byId = new Map(demandes.map((item) => [item.id, item]));
        const childrenByParent = new Map();
        demandes.forEach((item) => {
            if (item.previousRequestId && byId.has(item.previousRequestId)) {
                childrenByParent.set(item.previousRequestId, [...(childrenByParent.get(item.previousRequestId) || []), item]);
            }
        });
        const seen = new Set();
        return filtered.reduce((rows, item) => {
            const rootId = item.previousRequestId || item.id;
            if (seen.has(rootId)) return rows;
            seen.add(rootId);
            const requests = filtered.filter((request) => (request.previousRequestId || request.id) === rootId);
            const folder = rootId !== item.id || childrenByParent.has(rootId);
            rows.push({ folder, key: rootId, requests });
            return rows;
        }, []);
    })();

    const toggleLeaveFolder = (id) => setClosedLeaveFolders((current) => {
        const next = new Set(current);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    if (loading && demandes.length === 0) {
        return (
            <div className="tcPage">
                <div className="tcWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                <div className="tnfHeader">
                    <div>
                        <h1>{t("Traitement des demandes de congés")}</h1>
                        <p>{t("Gérez et validez les demandes de congés et absences de vos collaborateurs")}</p>
                    </div>
                </div>

                <div className="tnfCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Collaborateur / Matricule
                            </label>
                            <SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collaborateurOptions} placeholder={t("Nom ou matricule...")} />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Type de congé
                            </label>
                            <SearchableCombobox value={filterType} onChange={setFilterType} options={typeCongeOptions} placeholder={t("Rechercher un type...")} />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Date début
                            </label>
                            <input type="date" value={filterDateDebut} onChange={(e) => setFilterDateDebut(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Date fin
                            </label>
                            <input type="date" value={filterDateFin} onChange={(e) => setFilterDateFin(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" onClick={handleResetFilters} className="dcBtn dcBtn--outline" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}>
                                <RotateCcw size={14} /> Réinitialiser
                            </button>
                            <button type="button" className="dcBtn dcBtn--outline treatmentExportBtn" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }} onClick={() => dispatch(exportLeaveRequestsExcelRequest("admin_inbox"))}>
                                <Download size={14} /> Exporter Excel
                            </button>
                        </div>
                    </div>
                </div>

                {apiError && <div style={{ marginTop: 8, color: "#ef4444", fontSize: 12 }}>{safeErrorText(apiError)}</div>}

                <div className="tnfCard tnfCard--noPad">
                    <div className="tnfTabs">
                        {tabs.map((tab) => (
                            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tnfEmpty">
                                <AlertCircle size={46} />
                                <div>{t("Aucune demande trouvée")}</div>
                            </div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                <tr>
                                    <th>{t("Matricule")}</th>
                                    <th>{t("Collaborateur")}</th>
                                    <th>{t("Remplaçant")}</th>
                                    <th>{t("Type")}</th>
                                    <th>{t("Période")}</th>
                                    <th>{t("Durée")}</th>
                                    {(activeTab === "approuve" || activeTab === "rejete") && <th>{t("Traitée par")}</th>}
                                    {activeTab === "rejete" && <th>{t("Motif")}</th>}
                                    <th className="is-right">{t("Actions")}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {leaveRows.map((row) => (
                                    <React.Fragment key={`leave-row-${row.key}`}>
                                        {row.folder && <tr className="tnfLeaveFolderHeader"><td colSpan={20} style={{ padding: "7px 12px" }}><button type="button" onClick={() => toggleLeaveFolder(row.key)} style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: "#1e3a5f", fontWeight: 700, fontSize: 12 }}><ChevronDown size={16} style={{ transform: closedLeaveFolders.has(row.key) ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform .15s" }} />Dossier de congé DC-{String(row.key).padStart(6, "0")} · {row.requests[0].matricule} · {row.requests[0].nom} · {row.requests.length} demandes</button></td></tr>}
                                        {(!row.folder || !closedLeaveFolders.has(row.key)) && row.requests.map((demande) => (
                                    <tr key={demande.id} className={row.folder ? "tnfLeaveFolderItem" : ""} onClick={() => showDetails(demande)} style={{ cursor: "pointer" }}>
                                        <td><span className="tnfPrimary">{demande.matricule}</span></td>
                                        <td><span className="tnfPrimary">{demande.nom}</span></td>
                                        <td>
                                            <span className="tnfMuted">{demande.remplacantName || "—"}</span>
                                            {demande.remplacantCode && <div className="tnfSub">{demande.remplacantCode}</div>}
                                        </td>
                                        <td>
                                            <div className="tnfType">
                                                <span className="tnfDot" style={{ backgroundColor: "#3b82f6" }} />
                                                <span className="tnfPrimary">{row.folder ? `${demande.previousRequestId ? "Demande complémentaire" : "Demande principale"} · ${demande.typeConge}` : demande.typeConge}</span>
                                            </div>
                                            {!row.folder && linkedRequestLabel(demande) && <div style={{ display: "inline-block", marginTop: 4, padding: "3px 7px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontSize: 10, fontWeight: 700 }}>{linkedRequestLabel(demande)}</div>}
                                        </td>
                                        <td>
                                            <div className="tnfPrimary">{formatDate(demande.dateDebut)}</div>
                                            <div className="tnfSub">→ {formatDate(demande.dateFin)}</div>
                                            {demande.dateReprise && (
                                                <div style={{ fontSize: 11, color: "#059669", marginTop: 2, fontWeight: 500 }}>
                                                    Reprise : {formatDate(demande.dateReprise)}
                                                </div>
                                            )}
                                            {demande.originalDateDebut && (
                                                <div style={{ fontSize: 10, color: "#64748b", marginTop: 3 }}>
                                                    <span style={{ textDecoration: "line-through" }}>
                                                        {formatDate(demande.originalDateDebut)} → {formatDate(demande.originalDateFin)}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td><span className="tnfPrimary">{demande.duree} {t(demande.duree > 1 ? "jours" : "jour")}</span></td>
                                        {(activeTab === "approuve" || activeTab === "rejete") && (
                                            <td><span className="tnfMuted">{getRequestTreatedBy(demande)}</span></td>
                                        )}

                                        {activeTab === "rejete" && (
                                            <td>
                                                <span className="tnfMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {demande.adminComment || demande.managerComment || demande.motif || "—"}
                                                </span>
                                            </td>
                                        )}

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="tnfRowActions">
                                                {activeTab === "enCours" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn tnfActionBtn--ok"
                                                            onClick={() => handleOpenDecision(demande, "approve")}
                                                            title={t("Approuver")}
                                                            disabled={loading}
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn tnfActionBtn--no"
                                                            onClick={() => handleOpenDecision(demande, "reject")}
                                                            title={t("Refuser")}
                                                            disabled={loading}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                {activeTab === "approuve" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn"
                                                            onClick={() => handleDownloadLeavePdf(demande)}
                                                            title={t("Télécharger la fiche de congé")}
                                                            style={{ background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0" }}
                                                        >
                                                            <Download size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn"
                                                            onClick={() => handleOpenAdjust(demande)}
                                                            title={t("Retour anticipé (Ajuster la période)")}
                                                            style={{ background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}
                                                        >
                                                            <CalendarDays size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    type="button"
                                                    className="tnfActionBtn"
                                                    onClick={() => showDetails(demande)}
                                                    title={t("Voir les détails")}
                                                    style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon">
                                    <CalendarDays size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">{t("Détails de la demande")} #{selectedDemande.id}</div>
                                    <div className="tnfModal__sub">{t("Demande du")} {formatDate(selectedDemande.dateDebut)}</div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDetails} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            {selectedDemande.originalDateDebut && selectedDemande.rawStatus === "APPROVED" && (
                                <div className="tnfPanel tnfPanel--full" style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#d97706", fontWeight: "bold", fontSize: 14 }}>
                                        <AlertCircle size={18} />
                                        Congé Ajusté (Retour Anticipé)
                                    </div>
                                    <div style={{ marginTop: 6, fontSize: 12, color: "#b45309", lineHeight: 1.5 }}>
                                        Ce congé a été raccourci par l'administration RH.
                                        {t("La période planifiée initialement était du")} <b>{formatDate(selectedDemande.originalDateDebut)}</b> {t("au")} <b>{formatDate(selectedDemande.originalDateFin)}</b>.
                                        Les jours non consommés ont été restitués au solde de l'employé.
                                    </div>
                                </div>
                            )}
                            <div className="tnfGrid2">
                                {/* Infos collaborateur */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Informations collaborateur
                                    </div>
                                    <div className="tnfKv">
                                        <span>{t("Matricule")}</span>
                                        <b>{selectedDemande.matricule}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{t("Nom")}</span>
                                        <b>{selectedDemande.nom}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{t("Date de la demande")}</span>
                                        <b>{formatDate(selectedDemande.dateDemande)}</b>
                                    </div>
                                </div>

                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Remplaçant / intérim
                                    </div>
                                    <div className="tnfKv">
                                        <span>{t("Nom")}</span>
                                        <b>{selectedDemande.remplacantName || selectedDemande.replacement?.replacementUserName || "Non désigné"}</b>
                                    </div>
                                    {selectedDemande.replacement && (
                                        <div className="tnfKv"><span>{t("Disponibilité")}</span><b>{selectedDemande.replacement.status === "PENDING" ? "En attente" : selectedDemande.replacement.status === "ACCEPTED" ? "Accepté" : selectedDemande.replacement.status === "REFUSED" ? "Refusé" : selectedDemande.replacement.status === "AUTO_ACCEPTED" ? "Auto-accepté" : "Annulé"}</b></div>
                                    )}
                                    {selectedDemande.replacement?.status === "CANCELLED" && <div className="tnfKv"><span>{t("Motif du retrait")}</span><b style={{ color: "#b91c1c" }}>{selectedDemande.replacement.cancellationComment || "—"}</b></div>}
                                    {selectedDemande.remplacantCode && (
                                        <div className="tnfKv"><span>{t("Matricule")}</span><b>{selectedDemande.remplacantCode}</b></div>
                                    )}
                                    {selectedDemande.remplacantJobName && (
                                        <div className="tnfKv"><span>{t("Poste")}</span><b>{selectedDemande.remplacantJobName}</b></div>
                                    )}
                                    {selectedDemande.remplacantEntityName && (
                                        <div className="tnfKv"><span>{t("Service")}</span><b>{selectedDemande.remplacantEntityName}</b></div>
                                    )}
                                </div>

                                {/* Détails congé */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <CalendarDays size={16} /> Détails du congé
                                    </div>
                                    <div className="tnfKv">
                                        <span>{t("Type")}</span>
                                        <b>{selectedDemande.typeConge}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{t("Nature")}</span>
                                        <b>{selectedDemande.nature === "ABSENCE" ? "Absence" : "Congé"}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{t("Heure de sortie")}</span>
                                        <b>{selectedDemande.heureSortie ? String(selectedDemande.heureSortie).slice(0, 5) : "—"}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{selectedDemande.originalDateDebut ? "Nouvelle Date début" : "Date de début"}</span>
                                        <b className="tnfAccent">{formatDate(selectedDemande.dateDebut)}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{selectedDemande.originalDateFin ? "Nouvelle Date fin" : "Date de fin"}</span>
                                        <b className="tnfAccent">{formatDate(selectedDemande.dateFin)}</b>
                                    </div>
                                    {selectedDemande.dateReprise && (
                                        <div className="tnfKv">
                                            <span>{t("Date de reprise")}</span>
                                            <b style={{ color: "#059669" }}>{formatDate(selectedDemande.dateReprise)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.originalDateDebut && (
                                        <div className="tnfKv" style={{ background: "#fffbeb", padding: "4px 8px", borderRadius: 6, marginTop: 4 }}>
                                            <span style={{ color: "#92400e" }}>{t("Ancienne période")}</span>
                                            <b style={{ color: "#92400e", textDecoration: "line-through" }}>
                                                {formatDate(selectedDemande.originalDateDebut)} → {formatDate(selectedDemande.originalDateFin)}
                                            </b>
                                        </div>
                                    )}
                                    <div className="tnfKv">
                                        <span>{t("Durée")}</span>
                                        <b>{selectedDemande.duree} {t(selectedDemande.duree > 1 ? "jours" : "jour")}</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tnfPanel tnfPanel--full">
                                    <div className="tnfPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tnfText">{selectedDemande.motif}</div>
                                </div>

                                {/* Justificatifs */}
                                {(selectedDemande.justificatif || (selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0)) && (
                                    <div className="tnfPanel tnfPanel--full">
                                        <div className="tnfPanel__title">
                                            <FileText size={16} /> Justificatifs fournis
                                        </div>
                                        <div className="tnfDocs" style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" }}>
                                            {/* Support de l'ancien champ (string) */}
                                            {selectedDemande.justificatif && !selectedDemande.justificatifs?.includes(selectedDemande.justificatif) && (
                                                <div className="tnfDoc" onClick={() => handlePreview(selectedDemande.justificatif)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {selectedDemande.justificatif.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#3b82f6" />
                                                </div>
                                            )}
                                            {/* Support du nouveau champ (liste) */}
                                            {selectedDemande.justificatifs?.map((j, idx) => (
                                                <div key={idx} className="tnfDoc" onClick={() => handlePreview(j)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {j.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#3b82f6" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Décision manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tnfPanel tnfPanel--full">
                                        <div className="tnfPanel__title">
                                            <Check size={16} /> Validation Manager
                                        </div>
                                        <div className="tnfKv">
                                            <span>{t("Décision")}</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tnfKv">
                                                <span>{t("Par")}</span>
                                                <b>{selectedDemande.managerName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tnfKv">
                                                <span>{t("Date")}</span>
                                                <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tnfRejectBox">
                                                <div className="tnfRejectLabel">{t("Commentaire")}</div>
                                                <div className="tnfRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation RH */}
                                {/*{selectedDemande.statut !== "enCours" && selectedDemande.adminDecision !== null && (*/}
                                {/*    <div className={`tnfPanel tnfPanel--full ${selectedDemande.statut === "approuve" ? "tnfValidOk" : "tnfValidNo"}`}>*/}
                                {/*        <div className="tnfPanel__title">*/}
                                {/*            {selectedDemande.statut === "approuve" ? <Check size={16} /> : <X size={16} />}*/}
                                {/*            {selectedDemande.statut === "approuve" ? " Demande approuvée" : " Demande rejetée"}*/}
                                {/*        </div>*/}
                                {/*        <div className="tnfKv">*/}
                                {/*            <span>Date de validation</span>*/}
                                {/*            <b>{formatDate(selectedDemande.adminDecisionAt)}</b>*/}
                                {/*        </div>*/}
                                {/*        {selectedDemande.adminName && (*/}
                                {/*            <div className="tnfKv">*/}
                                {/*                <span>Validé par</span>*/}
                                {/*                <b>{selectedDemande.adminName}</b>*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*        {selectedDemande.adminComment && (*/}
                                {/*            <div className="tnfRejectBox">*/}
                                {/*                <div className="tnfRejectLabel">*/}
                                {/*                    {selectedDemande.statut === "approuve" ? "Commentaire" : "Motif du rejet"}*/}
                                {/*                </div>*/}
                                {/*                <div className="tnfRejectText">{selectedDemande.adminComment}</div>*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*    </div>*/}
                                {/*)}*/}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tnfModal__footer">
                            {selectedDemande.statut === "enCours" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "reject");
                                        }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ok"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "approve");
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* DECISION MODAL (Approuver ou Refuser) */}
            {showDecisionModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className={`tnfModal__icon ${pendingDecision === "reject" ? "tnfModal__icon--danger" : ""}`}>
                                    {pendingDecision === "approve" ? <Check size={18} /> : <AlertCircle size={18} />}
                                </div>
                                <div>
                                    <div className="tnfModal__title">
                                        {pendingDecision === "approve" ? "Approuver la demande de congé" : "Refuser la demande de congé"}
                                    </div>
                                    <div className="tnfModal__sub">
                                        {selectedDemande.nom} — {selectedDemande.typeConge}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDecision} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard">
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">{t("Période")}</span>
                                    <b>{formatDate(selectedDemande.dateDebut)} → {formatDate(selectedDemande.dateFin)}</b>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">{t("Durée")}</span>
                                    <b className="tnfAccent">{selectedDemande.duree} {t(selectedDemande.duree > 1 ? "jours" : "jour")}</b>
                                </div>
                            </div>

                            <label className="tnfLabel">
                                {pendingDecision === "reject" ? (
                                    <>{t("Motif du refus")} <span className="tnfReq">*</span></>
                                ) : (
                                    <>{t("Commentaire")} <span className="tnfMuted">({t("optionnel")})</span></>
                                )}
                            </label>
                            <textarea
                                value={decisionComment}
                                required
                                onChange={(e) => setDecisionComment(e.target.value)}
                                rows={5}
                                placeholder={
                                    pendingDecision === "reject"
                                        ? "Veuillez préciser le motif du refus de cette demande..."
                                        : "Ajouter un commentaire (optionnel)..."
                                }
                                className="tnfTextarea"
                            />
                            <div className="tnfHelp">{t("Ce commentaire sera communiqué au collaborateur")}</div>
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDecision}>
                                Annuler
                            </button>

                            {pendingDecision === "approve" ? (
                                <button
                                    type="button"
                                    className="tnfBtn tnfBtn--ok"
                                    onClick={confirmDecision}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer l'approbation"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="tnfBtn tnfBtn--danger"
                                    onClick={confirmDecision}
                                    disabled={!decisionComment.trim() || loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* PREVIEW MODAL */}
            {previewDoc && (
                <div className="tnfModal" style={{ zIndex: 10001 }}>
                    <div className="tnfModal__panel tnfModal__panel--wide" style={{ maxWidth: "90vw", width: "1000px" }}>
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon"><FileText size={18} /></div>
                                <div>
                                    <div className="tnfModal__title">{t("Aperçu du document")}</div>
                                    <div className="tnfModal__sub">{previewDoc.name}</div>
                                </div>
                            </div>
                            <button type="button" className="tnfIconBtn" onClick={closePreview}><X size={18} /></button>
                        </div>
                        <div className="tnfModal__body" style={{ height: "75vh", padding: 0, overflow: "hidden", background: "#f1f5f9", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            {previewDoc.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                                    <img src={previewDoc.url} alt="Aperçu" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)" }} />
                                </div>
                            ) : previewDoc.name.match(/\.pdf$/i) ? (
                                <iframe src={previewDoc.url} title={t("Aperçu PDF")} width="100%" height="100%" style={{ border: "none" }} />
                            ) : (
                                <div style={{ textAlign: "center", padding: 40 }}>
                                    <AlertCircle size={48} color="#64748b" style={{ marginBottom: 16 }} />
                                    <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 8 }}>{t("Aperçu non disponible")}</div>
                                    <p style={{ color: "#64748b", marginBottom: 24 }}>{t("Ce type de fichier ne peut pas être prévisualisé directement.")}</p>
                                    <button className="tnfBtn tnfBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                        <Download size={16} /> Télécharger pour consulter
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="tnfModal__footer">
                            <button className="tnfBtn tnfBtn--ghost" onClick={closePreview}>{t("Fermer")}</button>
                            <button className="tnfBtn tnfBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                <Download size={16} /> Télécharger le document
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ADJUST MODAL (Retour anticipé / Modification de période) */}
            {showAdjustModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon" style={{ background: "#fffbeb", color: "#d97706" }}>
                                    <CalendarDays size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">{t("Retour anticipé / Ajuster le congé")}</div>
                                    <div className="tnfModal__sub">
                                        {selectedDemande.nom} — {selectedDemande.typeConge}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tnfIconBtn" onClick={closeAdjust} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard" style={{ marginBottom: 16 }}>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">{t("Période initiale")}</span>
                                    <b>{formatDate(selectedDemande.dateDebut)} → {formatDate(selectedDemande.dateFin)}</b>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">{t("Durée initiale")}</span>
                                    <b className="tnfAccent">{selectedDemande.duree} {t(selectedDemande.duree > 1 ? "jours" : "jour")}</b>
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                                <div className="tnfField">
                                    <label className="tnfLabel">{t("Nouvelle date de début")} <span className="tnfReq">*</span></label>
                                    <input
                                        type="date"
                                        value={adjustStartDate}
                                        onChange={(e) => setAdjustStartDate(e.target.value)}
                                        className="tnfInput"
                                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box" }}
                                    />
                                </div>

                                <div className="tnfField">
                                    <label className="tnfLabel">{t("Nouvelle date de fin")} <span className="tnfReq">*</span></label>
                                    <input
                                        type="date"
                                        value={adjustEndDate}
                                        onChange={(e) => {
                                            const newEnd = e.target.value;
                                            setAdjustEndDate(newEnd);
                                            setAdjustDateReprise(newEnd ? getProchainJourOuvre(newEnd) : "");
                                        }}
                                        className="tnfInput"
                                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box" }}
                                    />
                                </div>
                            </div>

                            <div className="tnfField" style={{ marginBottom: 16 }}>
                                <label className="tnfLabel">{t("Date de reprise du travail (automatique)")}</label>
                                <input
                                    type="text"
                                    readOnly
                                    value={adjustDateReprise ? formatDate(adjustDateReprise) : "—"}
                                    className="tnfInput"
                                    style={{
                                        width: "100%",
                                        padding: "8px 12px",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: 6,
                                        boxSizing: "border-box",
                                        backgroundColor: "#f8fafc",
                                        color: "#059669",
                                        fontWeight: 600,
                                        cursor: "default"
                                    }}
                                />
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                                    Suit automatiquement la date de fin (premier jour ouvré suivant, hors week-ends et jours fériés).
                                </div>
                            </div>

                            {/* Dynamically calculate & show refund */}
                            {(() => {
                                if (!adjustStartDate || !adjustEndDate) return null;
                                const start = new Date(adjustStartDate);
                                const end = new Date(adjustEndDate);
                                if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

                                const diffTime = end.getTime() - start.getTime();
                                const newDur = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;

                                if (newDur <= 0) {
                                    return (
                                        <div style={{ color: "#ef4444", fontSize: 13, fontWeight: "500", marginTop: 8 }}>
                                            ⚠️ La date de fin doit être supérieure ou égale à la date de début.
                                        </div>
                                    );
                                }

                                if (newDur > selectedDemande.duree) {
                                    return (
                                        <div style={{ color: "#ef4444", fontSize: 13, fontWeight: "500", marginTop: 8 }}>
                                            ⚠️ La nouvelle durée ({newDur} j) ne peut pas dépasser la durée initiale ({selectedDemande.duree} j).
                                        </div>
                                    );
                                }

                                const refund = selectedDemande.duree - newDur;
                                return (
                                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 12, marginTop: 12 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                                            <span style={{ color: "#166534" }}>{t("Nouvelle durée")} :</span>
                                            <b style={{ color: "#166534" }}>{newDur} {t(newDur > 1 ? "jours" : "jour")}</b>
                                        </div>
                                        {refund > 0 ? (
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: "bold" }}>
                                                <span style={{ color: "#15803d" }}>{t("Jours à restituer au solde")} :</span>
                                                <span style={{ color: "#15803d" }}>+{refund} {t(refund > 1 ? "jours" : "jour")}</span>
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: 12, color: "#166534", fontStyle: "italic" }}>
                                                Aucune modification de durée (les dates sont identiques).
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeAdjust}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tnfBtn tnfBtn--ok"
                                onClick={confirmAdjust}
                                disabled={
                                    loading ||
                                    !adjustStartDate ||
                                    !adjustEndDate ||
                                    (Math.round((new Date(adjustEndDate) - new Date(adjustStartDate)) / (1000 * 60 * 60 * 24)) + 1) <= 0 ||
                                    (Math.round((new Date(adjustEndDate) - new Date(adjustStartDate)) / (1000 * 60 * 60 * 24)) + 1) > selectedDemande.duree
                                }
                                style={{ background: "#d97706", borderColor: "#d97706" }}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer l'ajustement"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementConges;
